import React, { useEffect, useState } from 'react';
import { getCategories, adminCreateCategory, adminUpdateCategory, adminDeleteCategory } from '../../api';
import './AdminLayout.css';

const EMPTY = { name: '', slug: '', description: '', order: 0 };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | category object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  function load() {
    setLoading(true);
    getCategories().then(r => { setCategories(r.data); setLoading(false); });
  }

  useEffect(() => { load(); }, []);

  function openNew() { setForm(EMPTY); setModal('new'); }
  function openEdit(cat) { setForm({ name: cat.name, slug: cat.slug, description: cat.description || '', order: cat.order || 0 }); setModal(cat); }

  function set(key, val) {
    setForm(f => ({ ...f, [key]: val }));
    if (key === 'name' && modal === 'new') {
      const slug = val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setForm(f => ({ ...f, name: val, slug }));
    }
  }

  async function handleSave() {
    if (!form.name.trim()) return alert('Name is required');
    setSaving(true);
    try {
      if (modal === 'new') {
        const { data } = await adminCreateCategory(form);
        setCategories(prev => [...prev, data]);
      } else {
        const { data } = await adminUpdateCategory(modal._id, form);
        setCategories(prev => prev.map(c => c._id === modal._id ? data : c));
      }
      setModal(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(cat) {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await adminDeleteCategory(cat._id);
      setCategories(prev => prev.filter(c => c._id !== cat._id));
    } catch {
      alert('Delete failed');
    }
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Categories</h1>
          <p className="admin-page-sub">{categories.length} categories</p>
        </div>
        <button className="btn-primary" onClick={openNew}>+ Add Category</button>
      </div>

      <div style={{ padding: '16px 28px 28px' }}>
        <div className="admin-card">
          {loading ? (
            <div className="admin-loading">Loading…</div>
          ) : categories.length === 0 ? (
            <div className="empty-state">
              <span>🗂️</span>
              <p>No categories yet.</p>
              <button className="btn-primary" onClick={openNew}>Add first category</button>
            </div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr><th>Name</th><th>Slug</th><th>Description</th><th>Order</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {categories.map(cat => (
                    <tr key={cat._id}>
                      <td><strong>{cat.name}</strong></td>
                      <td style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: 13 }}>{cat.slug}</td>
                      <td style={{ color: '#6b7280', fontSize: 13 }}>{cat.description || '—'}</td>
                      <td>{cat.order}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-icon" onClick={() => openEdit(cat)}>✏️ Edit</button>
                          <button className="btn-danger" onClick={() => handleDelete(cat)}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modal === 'new' ? 'Add Category' : 'Edit Category'}</h2>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Keychains" />
              </div>
              <div className="form-group">
                <label>Slug</label>
                <input value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="auto-generated" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Short description…" />
              </div>
              <div className="form-group">
                <label>Sort Order</label>
                <input type="number" value={form.order} onChange={e => set('order', Number(e.target.value))} />
                <span className="form-hint">Lower number = appears first in nav</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : modal === 'new' ? 'Create Category' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
